import { ANNOTATION_LOCATION, GroupEntity } from '@backstage/catalog-model';
import {
  buildGroupIndex,
  readJiraProjectKeys,
  resolveInheritedProjectKeys,
} from './organizationBoardResolution';

export type OrganizationGroupRow = {
  entityRef: string;
  name: string;
  displayName: string;
  type: string;
  parent?: string;
  manager?: string;
  catalogPath: string;
  /** Keys shown in the tree: the group's own declared keys, or inherited ones. */
  projectKeys: string[];
  /** Only the group's own declared keys (empty when it merely inherits). */
  declaredProjectKeys: string[];
  /** True when `projectKeys` come from a parent rather than this group. */
  inheritsBoards: boolean;
};

export type OrganizationGroupNode = OrganizationGroupRow & {
  children: OrganizationGroupNode[];
};

function displayNameOf(entity: GroupEntity): string {
  return entity.spec.profile?.displayName ?? entity.metadata.title ?? entity.metadata.name;
}

function managerLabel(manager?: string): string | undefined {
  if (!manager) {
    return undefined;
  }
  return manager.replace(/^user:/i, '');
}

function projectKeysFromEntity(entity: GroupEntity): string[] {
  return readJiraProjectKeys(entity);
}

export function buildOrganizationGroupRows(entities: GroupEntity[]): OrganizationGroupRow[] {
  return entities.map(entity => {
    const namespace = entity.metadata.namespace ?? 'default';
    const parent = entity.spec.parent;
    const declaredKeys = projectKeysFromEntity(entity);

    return {
      entityRef: `group:${namespace}/${entity.metadata.name}`,
      name: entity.metadata.name,
      displayName: displayNameOf(entity),
      type: entity.spec.type ?? 'unknown',
      parent: typeof parent === 'string' ? parent : undefined,
      manager: managerLabel((entity.spec as GroupEntity['spec'] & { manager?: string }).manager),
      catalogPath: `/catalog/${namespace}/group/${entity.metadata.name}`,
      projectKeys: declaredKeys,
      declaredProjectKeys: declaredKeys,
      inheritsBoards: false,
    };
  });
}

function sortOrganizationGroupNodes(nodes: OrganizationGroupNode[]): void {
  nodes.sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));
  nodes.forEach(node => sortOrganizationGroupNodes(node.children));
}

export function enrichOrganizationGroupRows(groups: GroupEntity[]): OrganizationGroupRow[] {
  const groupIndex = buildGroupIndex(groups);
  const groupsByName = new Map(groups.map(group => [group.metadata.name, group]));

  return buildOrganizationGroupRows(groups).map(row => {
    const groupEntity = groupsByName.get(row.name);
    const declaredKeys = groupEntity ? readJiraProjectKeys(groupEntity) : row.declaredProjectKeys;
    if (declaredKeys.length > 0) {
      return {
        ...row,
        projectKeys: declaredKeys,
        declaredProjectKeys: declaredKeys,
        inheritsBoards: false,
      };
    }

    const inherited = resolveInheritedProjectKeys(row.name, groupIndex, groupName => {
      const group = groupsByName.get(groupName);
      return group ? readJiraProjectKeys(group) : [];
    });

    return {
      ...row,
      projectKeys: inherited.projectKeys,
      declaredProjectKeys: [],
      inheritsBoards: inherited.projectKeys.length > 0,
    };
  });
}

export function buildOrganizationGroupTree(rows: OrganizationGroupRow[]): OrganizationGroupNode[] {
  const nodes = new Map<string, OrganizationGroupNode>();

  for (const row of rows) {
    nodes.set(row.name, { ...row, children: [] });
  }

  const roots: OrganizationGroupNode[] = [];

  for (const node of nodes.values()) {
    const parent = node.parent ? nodes.get(node.parent) : undefined;
    if (parent) {
      parent.children.push(node);
      continue;
    }

    // Any node without a resolvable parent surfaces as a root, regardless of
    // type. This includes teams whose spec.parent points to a group outside the
    // loaded set, so orphans never silently disappear from the tree or counts.
    roots.push(node);
  }

  sortOrganizationGroupNodes(roots);
  return roots;
}

function nodeMatchesSearch(node: OrganizationGroupRow, searchTerm: string): boolean {
  return [node.name, node.displayName, node.type, node.parent, node.manager, ...node.projectKeys]
    .filter(Boolean)
    .some(value => value!.toLowerCase().includes(searchTerm));
}

function filterOrganizationGroupNode(
  node: OrganizationGroupNode,
  searchTerm: string,
): OrganizationGroupNode | undefined {
  if (nodeMatchesSearch(node, searchTerm)) {
    return node;
  }

  const filteredChildren = node.children
    .map(child => filterOrganizationGroupNode(child, searchTerm))
    .filter((child): child is OrganizationGroupNode => child !== undefined);

  if (filteredChildren.length === 0) {
    return undefined;
  }

  return {
    ...node,
    children: filteredChildren,
  };
}

export function filterOrganizationGroupTree(
  nodes: OrganizationGroupNode[],
  searchTerm: string,
): OrganizationGroupNode[] {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) {
    return nodes;
  }

  return nodes
    .map(node => filterOrganizationGroupNode(node, normalizedSearch))
    .filter((node): node is OrganizationGroupNode => node !== undefined);
}

export function countOrganizationGroupNodes(nodes: OrganizationGroupNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countOrganizationGroupNodes(node.children), 0);
}

export const ROOT_ORGANIZATION_NAME = 'invisible-tech';

export function isInvisibleOrgGroup(entity: GroupEntity): boolean {
  if (entity.metadata.name === ROOT_ORGANIZATION_NAME) {
    return true;
  }

  const location =
    entity.metadata.annotations?.[ANNOTATION_LOCATION] ??
    entity.metadata.annotations?.['backstage.io/managed-by-origin-location'];
  return Boolean(location?.toLowerCase().includes('invisibleorg'));
}

export function splitRootOrganization(nodes: OrganizationGroupNode[]): {
  root?: OrganizationGroupNode;
  tree: OrganizationGroupNode[];
} {
  if (nodes.length === 1 && nodes[0].name === ROOT_ORGANIZATION_NAME) {
    return {
      root: nodes[0],
      tree: nodes[0].children,
    };
  }

  return { tree: nodes };
}

export function collectOrganizationGroupNodeNames(nodes: OrganizationGroupNode[]): Set<string> {
  const names = new Set<string>();
  const visit = (node: OrganizationGroupNode) => {
    names.add(node.name);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return names;
}

export function filterOrganizationGroupRows(
  rows: OrganizationGroupRow[],
  searchTerm: string,
): OrganizationGroupRow[] {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) {
    return rows;
  }

  return rows.filter(row => nodeMatchesSearch(row, normalizedSearch));
}
